<template>
  <div class="step-summary">
    <v-container fluid class="pa-6 px-4">
      <v-row justify="center">
        <v-col cols="12" lg="11">
          <div v-if="!hasIssues" class="text-center py-8">
            <v-icon size="80" color="success" class="mb-4">mdi-check-circle</v-icon>
            <div class="text-h5 font-weight-medium mb-2">Everything looks good!</div>
            <div class="text-body-1 grey--text mb-6">
              Your file has <strong>{{ totalRows }}</strong> product{{ totalRows !== 1 ? 's' : '' }} and is ready to import.
            </div>

            <v-alert v-if="templateWarning" type="info" outlined class="text-left mb-6" style="max-width: 600px; margin: 0 auto;">
              <div class="text-body-2 mb-2">
                Your file doesn't include the following optional columns. This is fine if your products don't have trays or cases:
              </div>
              <div class="d-flex flex-wrap">
                <v-chip 
                  v-for="header in templateWarning.missingOptional" 
                  :key="header"
                  small
                  outlined
                  color="grey"
                  class="mr-2 mb-1"
                >
                  {{ header }}
                </v-chip>
              </div>
            </v-alert>

            <v-btn color="primary" large @click="$emit('skip-to-review')">
              Continue to Import
              <v-icon right>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <div v-else>
            <div class="summary-intro mb-6">
              <div class="text-body-1 mb-2">
                We found {{ totalIssueCount === 1 ? 'one thing' : 'a few things' }} to fix in your file. We'll help you sort {{ totalIssueCount === 1 ? 'it' : 'them' }} out before importing.
              </div>
              <div class="text-body-2 grey--text">
                Don't worry - we'll walk you through {{ totalIssueCount === 1 ? 'it' : 'each one' }} step by step.
              </div>
            </div>

            <v-row class="mb-6">
              <v-col cols="12" sm="6" md="4">
                <v-card outlined class="summary-card--info">
                  <v-card-text class="d-flex align-center pa-4">
                    <div class="summary-icon summary-icon--info mr-4">
                      <v-icon color="white" size="28">mdi-file-document-outline</v-icon>
                    </div>
                    <div>
                      <div class="text-h5 font-weight-bold">{{ totalRows }}</div>
                      <div class="text-body-2 grey--text">Products in your file</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-card outlined class="summary-card--warning">
                  <v-card-text class="d-flex align-center pa-4">
                    <div class="summary-icon summary-icon--warning mr-4">
                      <v-icon color="white" size="28">mdi-alert-circle-outline</v-icon>
                    </div>
                    <div>
                      <div class="text-h5 font-weight-bold">{{ totalIssueCount }}</div>
                      <div class="text-body-2 grey--text">Issues to review</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <div class="text-h6 font-weight-medium mb-4">What we found:</div>

            <div class="issue-list">
              <v-card 
                v-if="duplicateCount > 0" 
                outlined 
                class="mb-3"
              >
                <v-card-text class="d-flex align-start pa-4">
                  <v-icon color="error" size="32" class="mr-4 mt-1">mdi-content-duplicate</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-medium mb-1">
                      {{ duplicateCount }} product{{ duplicateCount > 1 ? 's appear' : ' appears' }} more than once
                    </div>
                    <div class="text-body-2 grey--text">
                      You'll be able to choose which version to keep for each duplicate.
                    </div>
                  </div>
                  <v-chip small color="error" text-color="white">
                    {{ duplicateCount }}
                  </v-chip>
                </v-card-text>
              </v-card>

              <v-card 
                v-if="missingFieldsCount > 0" 
                outlined 
                class="mb-3"
              >
                <v-card-text class="d-flex align-start pa-4">
                  <v-icon color="error" size="32" class="mr-4 mt-1">mdi-pound-box-outline</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-medium mb-1">
                      {{ missingFieldsCount }} required field{{ missingFieldsCount > 1 ? 's are' : ' is' }} missing
                    </div>
                    <div class="text-body-2 grey--text">
                      Every asset row must include an Identifier before the import can continue.
                    </div>
                  </div>
                  <v-chip small color="error" text-color="white">
                    {{ missingFieldsCount }}
                  </v-chip>
                </v-card-text>
              </v-card>

              <v-card 
                v-if="whitespaceCount > 0" 
                outlined 
                class="mb-3"
              >
                <v-card-text class="d-flex align-start pa-4">
                  <v-icon color="error" size="32" class="mr-4 mt-1">mdi-format-clear</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-medium mb-1">
                      {{ whitespaceCount }} value{{ whitespaceCount > 1 ? 's have' : ' has' }} extra spaces or line breaks
                    </div>
                    <div class="text-body-2 grey--text">
                      We can clean these up automatically.
                    </div>
                  </div>
                  <v-chip small color="error" text-color="white">
                    {{ whitespaceCount }}
                  </v-chip>
                </v-card-text>
              </v-card>

              <v-card 
                v-if="harmonisationCount > 0" 
                outlined 
                class="mb-3"
              >
                <v-card-text class="d-flex align-start pa-4">
                  <v-icon color="warning" size="32" class="mr-4 mt-1">mdi-format-letter-case</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-medium mb-1">
                      {{ harmonisationCount }} name{{ harmonisationCount > 1 ? 's are' : ' is' }} spelled differently but look{{ harmonisationCount === 1 ? 's' : '' }} the same
                    </div>
                    <div class="text-body-2 grey--text">
                      For example: {{ harmonisationExample }}. You'll be able to pick which spelling to use.
                    </div>
                  </div>
                  <v-chip small color="warning" text-color="white">
                    {{ harmonisationCount }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </div>

            <v-alert v-if="templateWarning" type="info" outlined class="mt-6">
              <div class="text-body-2 mb-2">
                Your file doesn't include the following optional columns. This is fine if your products don't have trays or cases:
              </div>
              <div class="d-flex flex-wrap">
                <v-chip 
                  v-for="header in templateWarning.missingOptional" 
                  :key="header"
                  small
                  outlined
                  color="grey"
                  class="mr-2 mb-1"
                >
                  {{ header }}
                </v-chip>
              </div>
            </v-alert>

            <div class="d-flex justify-center mt-8">
              <v-btn color="primary" x-large @click="$emit('continue')">
                Let's Get Started
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'StepSummary',
  props: {
    validationResults: {
      type: Object,
      default: () => ({
        duplicates: [],
        missingFields: [],
        whitespaceIssues: [],
        harmonisationIssues: []
      })
    },
    totalRows: {
      type: Number,
      default: 0
    },
    templateWarning: {
      type: Object,
      default: null
    },
  },
  computed: {
    duplicateCount() {
      return (this.validationResults.duplicates && this.validationResults.duplicates.length) || 0;
    },
    missingFieldsCount() {
      return (this.validationResults.missingFields && this.validationResults.missingFields.length) || 0;
    },
    whitespaceCount() {
      return (this.validationResults.whitespaceIssues && this.validationResults.whitespaceIssues.length) || 0;
    },
    harmonisationCount() {
      return (this.validationResults.harmonisationIssues && this.validationResults.harmonisationIssues.length) || 0;
    },
    harmonisationExample() {
      const issues = this.validationResults.harmonisationIssues;
      if (issues && issues.length > 0) {
        const variants = issues[0].variants;
        return '"' + variants[0].value + '" vs "' + variants[1].value + '"';
      }
      return '"Value A" vs "Value B"';
    },
    totalIssueCount() {
      return this.duplicateCount + this.missingFieldsCount + this.whitespaceCount + this.harmonisationCount;
    },
    hasIssues() {
      return this.totalIssueCount > 0;
    }
  }
};
</script>

<style scoped>
.summary-intro {
  max-width: 670px;
}

.summary-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-icon--info {
  background: linear-gradient(135deg, #1976d2, #1565c0);
}

.summary-icon--warning {
  background: linear-gradient(135deg, #ef5350, #c62828);
}
</style>
